// 2-step picker dropdown (cases → écritures). Ported from the throwaway
// mockup `components/PaneCase.tsx:66-118` into Tailwind + logical-RTL + i18n.
//
// Pure presentation (W1): the lists + select actions are passed in by the
// parent `CaseStrip` (which reads them from `useCaseStore`); the picker holds
// NO store and NO Office.js (W2). BE-emitted client names / titles get
// per-string direction via `bidiProps` (CLAUDE.md §9.2 rule 4). Meta lines
// are interpolated (no `v{version}` concat — §9.3).

import { useTranslation } from 'react-i18next'
import { bidiProps } from '@/lib/bidi'
import type { CaseSummary, BriefSummary } from '@/domain/case/entity'

export type PickerStep = 'cases' | 'briefs'

export interface CasePickerProps {
  step: PickerStep
  loading: boolean
  cases: CaseSummary[]
  briefs: BriefSummary[]
  onPickCase: (caseId: string) => void
  onPickBrief: (briefId: string) => void
  onBack: () => void
}

export function CasePicker({
  step,
  loading,
  cases,
  briefs,
  onPickCase,
  onPickBrief,
  onBack,
}: CasePickerProps): JSX.Element {
  const { t } = useTranslation()

  return (
    <div
      role="listbox"
      data-testid="case-picker"
      className="absolute inset-inline-start-0 top-full z-10 mt-1 max-h-72 w-72 max-w-[calc(100vw-1.5rem)] overflow-y-auto rounded-md border border-ink/10 bg-white py-1 shadow-lg"
    >
      {loading && (
        <div
          data-testid="case-picker-loading"
          className="px-3 py-2 text-[11px] text-ink/60"
        >
          {t('case.picker.loading')}
        </div>
      )}

      {!loading && step === 'cases' && (
        <>
          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink/40">
            {t('case.picker.casesHeader')}
          </div>
          {cases.length === 0 && (
            <div className="px-3 py-2 text-[11px] text-ink/60">
              {t('case.picker.noCases')}
            </div>
          )}
          {cases.map((c) => (
            <button
              key={c.id}
              type="button"
              role="option"
              data-testid={`case-picker-case-${c.id}`}
              onClick={() => onPickCase(c.id)}
              className="flex w-full flex-col gap-0.5 px-3 py-1.5 text-start hover:bg-ink/5"
            >
              <span
                className="text-xs font-semibold text-ink"
                {...bidiProps(c.clientName)}
              >
                {c.clientName}
              </span>
              <span className="text-[10px] text-ink/60" {...bidiProps(c.caseType)}>
                {t('case.picker.caseMeta', {
                  caseType: c.caseType,
                  jurisdiction: c.jurisdictionPrimary,
                })}
              </span>
            </button>
          ))}
        </>
      )}

      {!loading && step === 'briefs' && (
        <>
          <button
            type="button"
            data-testid="case-picker-back"
            onClick={onBack}
            className="flex w-full items-center gap-1 px-3 py-1.5 text-start text-[11px] font-semibold text-ink/70 hover:bg-ink/5"
          >
            <span aria-hidden="true" className="inline-block rtl:-scale-x-100">
              ‹
            </span>
            {t('case.picker.backToCases')}
          </button>
          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink/40">
            {t('case.picker.briefsHeader')}
          </div>
          {briefs.length === 0 && (
            <div className="px-3 py-2 text-[11px] text-ink/60">
              {t('case.picker.noBriefs')}
            </div>
          )}
          {briefs.map((b) => (
            <button
              key={b.id}
              type="button"
              role="option"
              data-testid={`case-picker-brief-${b.id}`}
              onClick={() => onPickBrief(b.id)}
              className="flex w-full flex-col gap-0.5 px-3 py-1.5 text-start hover:bg-ink/5"
            >
              <span
                className="text-xs font-semibold text-ink"
                {...bidiProps(b.title)}
              >
                {b.title}
              </span>
              <span className="text-[10px] text-ink/60" {...bidiProps(b.type)}>
                {t('case.picker.briefMeta', {
                  type: b.type,
                  version: b.version,
                  lang: b.language.toUpperCase(),
                })}
              </span>
            </button>
          ))}
        </>
      )}
    </div>
  )
}
